import CryptoJS from 'crypto-js';

// Public key for encrypting the payload.
const PUBLIC_ENCRYPTION_KEY =
  process.env.NEXT_PUBLIC_SEARCH_ENCRYPTION_KEY || 'public-encryption-key-change-me';

export interface SearchPayload {
  query: string;
  regulationType?: string;
  governmentBody?: string;
  page: number;
  pageSize: number;
  timestamp: number;
  regulationIds?: string[];
}

export interface SnippetPayload {
  regulationId: string;
  query: string;
  timestamp: number;
}

export interface WaitingListPayload {
  email: string;
  task: string;
  timestamp: number;
}

export interface JudgmentSummarizationPayload {
  name?: string;
  year?: string;
  timestamp: number;
}

export interface RegulationSummarizationPayload {
  name?: string;
  year?: string;
  regulationId?: string;
  timestamp: number;
}

export interface ArticleSearchPayload {
  regulationId: string;
  query: string;
  timestamp: number;
}

// Union type for all possible encrypted payloads
export type EncryptedPayload =
  | SearchPayload
  | SnippetPayload
  | WaitingListPayload
  | JudgmentSummarizationPayload
  | RegulationSummarizationPayload
  | ArticleSearchPayload;

/**
 * Encrypts search parameters.
 * Can be used on client or server using the PUBLIC shared key.
 */
export function encryptSearchPayload(params: Omit<SearchPayload, 'timestamp'>): string {
  const payload: SearchPayload = {
    ...params,
    timestamp: Date.now(),
  };
  return CryptoJS.AES.encrypt(JSON.stringify(payload), PUBLIC_ENCRYPTION_KEY).toString();
}

/**
 * Encrypts judgment summarization parameters.
 */
export function encryptJudgmentSummarizationPayload(
  params: Omit<JudgmentSummarizationPayload, 'timestamp'>
): string {
  const payload: JudgmentSummarizationPayload = {
    ...params,
    timestamp: Date.now(),
  };
  return CryptoJS.AES.encrypt(JSON.stringify(payload), PUBLIC_ENCRYPTION_KEY).toString();
}

/**
 * Encrypts regulation summarization parameters.
 */
export function encryptRegulationSummarizationPayload(
  params: Omit<RegulationSummarizationPayload, 'timestamp'>
): string {
  const payload: RegulationSummarizationPayload = {
    ...params,
    timestamp: Date.now(),
  };
  return CryptoJS.AES.encrypt(JSON.stringify(payload), PUBLIC_ENCRYPTION_KEY).toString();
}

/**
 * Encrypts waiting list parameters.
 */
export function encryptWaitingListPayload(params: Omit<WaitingListPayload, 'timestamp'>): string {
  const payload: WaitingListPayload = {
    ...params,
    timestamp: Date.now(),
  };
  return CryptoJS.AES.encrypt(JSON.stringify(payload), PUBLIC_ENCRYPTION_KEY).toString();
}

/**
 * Encrypts snippet request parameters.
 */
export function encryptSnippetPayload(params: Omit<SnippetPayload, 'timestamp'>): string {
  const payload: SnippetPayload = {
    ...params,
    timestamp: Date.now(),
  };
  return CryptoJS.AES.encrypt(JSON.stringify(payload), PUBLIC_ENCRYPTION_KEY).toString();
}

/**
 * Encrypts article search parameters.
 */
export function encryptArticleSearchPayload(
  params: Omit<ArticleSearchPayload, 'timestamp'>
): string {
  const payload: ArticleSearchPayload = {
    ...params,
    timestamp: Date.now(),
  };
  return CryptoJS.AES.encrypt(JSON.stringify(payload), PUBLIC_ENCRYPTION_KEY).toString();
}
